/**
 * Attacker Activity Window Calculator
 *
 * Calculates the time window during which an attacker was active, based on
 * ALL successful events from malicious IPs (not just sign-ins).
 *
 * - Activity window: first → last successful event from any malicious IP
 * - Token cutoff: based on last successful sign-in + token lifetime, or remediation
 *
 * @param {Array} signInEvents - All sign-in events (unfiltered)
 * @param {Array} directoryAuditEvents - All directory audit events (unfiltered)
 * @param {Object} ipClassifications - { "1.2.3.4": "malicious", ... }
 * @param {number} tokenLifetimeMinutes - Per-tenant token lifetime (default 90)
 * @param {Object} allResults - Full results object with all event arrays
 * @param {Set} dismissedEventIds - Dismissed event ids (excluded from anchoring and activity)
 * @param {Object} [opts]
 * @param {Object} [opts.legAttribution] - analyzeRecoveredLegs() output. CaseDetail computes it
 *   once and passes it in; when absent the function builds it itself from the same inputs, so
 *   a caller that omits it gets the same answer.
 * @param {Map} [opts.analystEvents] - parseAttackerEventMarks() output (row marks).
 * @param {Map} [opts.attackerSessions] - attackerSessionMarks() output (utils/sessionMarks.js):
 *   sessions the analyst marked as the attacker's. Every non-failed row on one anchors the
 *   window, with no address, from the session's first row.
 * @returns {Object|null} Window calculation result, or null when nothing anchors a window:
 *   no IP classified malicious AND no successful anchoring leg
 */

import { analyzeRecoveredLegs, caseIdentityIds } from './signInTransactions.js';
import { buildAttackerContextFrom } from './attackerAttribution.js';
import { phishingKitRefusal } from './refusedAttempts.js';
import { assessAttackerAccess, accountStateHistory } from './attackerAccess.js';

// CAE (Continuous Access Evaluation) propagation time — independent of token lifetime
const CAE_PROPAGATION_MINUTES = 15;
const BUFFER_MINUTES = 30;

// Remediation operation name patterns (case-insensitive matching)
const DISABLE_OPS = ['disable account'];
const REVOKE_OPS = [
  'revoke sign in sessions',
  'invalidate all refresh tokens for a user',
  'update stsrefreshtokenvalidfrom timestamp',
];
const PASSWORD_OPS = [
  'reset user password',
  'change user password',
  'reset password (by admin)',
];

function addMinutes(dateStr, minutes) {
  const d = new Date(dateStr);
  return new Date(d.getTime() + minutes * 60000);
}

/**
 * Check if an event is from a malicious IP (direct or inferred).
 * Only checks IPAddress and InferredIP — NOT AllResolvedIPs.
 * For single-IP session resolution, IPAddress is already the resolved real IP.
 * For multi-IP sessions, we can't determine which IP performed the action,
 * so they don't extend the attacker window (user can dismiss them individually).
 */
function getMaliciousIP(evt, maliciousSet) {
  if (evt.IPAddress && maliciousSet.has(evt.IPAddress)) return evt.IPAddress;
  if (evt.InferredIP && maliciousSet.has(evt.InferredIP)) return evt.InferredIP;
  return null;
}

/**
 * Check if an event is a failed event (should be excluded from activity window).
 */
function isFailed(evt) {
  const status = (evt.ResultStatus || evt.Status || '').toLowerCase().trim();
  return status === 'failed' || status === 'failure';
}

export function calculateAttackerWindow(
  signInEvents = [],
  directoryAuditEvents = [],
  ipClassifications = {},
  tokenLifetimeMinutes = 90,
  allResults = null,
  dismissedEventIds = null,
  opts = {},
) {
  // Extract malicious IPs
  const maliciousIPs = Object.entries(ipClassifications)
    .filter(([, status]) => status === 'malicious')
    .map(([ip]) => ip);
  const maliciousSet = new Set(maliciousIPs);

  // Dismissed events are excluded — investigator determined them unrelated
  const dismissed = dismissedEventIds instanceof Set ? dismissedEventIds : new Set();

  const isSuccess = (evt) => {
    const status = (evt.ResultStatus || evt.Status || '').toLowerCase().trim();
    return status === 'success' || status === 'succeeded';
  };

  // 🔴 RECOVERED / PHISHING-KIT LEGS ANCHOR ONLY ON EXACT EVIDENCE (v1.188.0;
  // Plans/2026-09-30_Recovered-Legs-Transaction-Attribution.md). v1.181.0 anchored EVERY leg
  // matching `isPinnedSignInEvent` — UnattributedRecovery or a URL-shaped ApplicationId — and
  // UnattributedRecovery turned out to be Microsoft's ordinary logging for MFA sub-steps
  // (UserId "Not Available"): on IR-20260930-mendozap-001, 52 of the victim's own MFA legs,
  // from an address the analyst marked Safe, started the window 34 hours early. A leg now
  // anchors only when analyzeRecoveredLegs gives it the `attacker` verdict.
  //
  // Two different claims, kept apart (review finding R3):
  //   ANCHOR  — any `attacker` leg: it moves the window.
  //   CAPTURE — a URL-shaped application only: the one signal that the password and a session
  //             token were captured. `phishingKitLegCount`, `successfulPhishingKitLegCount` and
  //             `anchoredByPhishingKit` mean CAPTURE, because compromiseScope's "confirmed AiTM
  //             capture" premise and the reports' capture prose read them.
  //
  // Unchanged from v1.181.0: only a SUCCESSFUL leg anchors (a failed landing captured
  // nothing), dismissed legs are excluded, and a leg's address is never added to
  // `maliciousIPs` — it is usually the victim's own.
  const legAttribution = opts?.legAttribution || analyzeRecoveredLegs(signInEvents, {
    maliciousSet,
    attackerSessions: buildAttackerContextFrom(allResults || { SignInEvents: signInEvents }, maliciousSet, null,
      opts?.attackerSessions instanceof Map ? opts.attackerSessions : null).attackerSessions,
    identityIds: caseIdentityIds(allResults),
  });
  const verdictOf = (evt) => legAttribution.byEventId.get(evt.EventId);
  const anchorLegs = signInEvents.filter((evt) => {
    if (dismissed.has(evt.EventId)) return false;
    return verdictOf(evt)?.verdict === 'attacker';
  });
  const phishingKitLegs = anchorLegs.filter((evt) => verdictOf(evt).capture);
  const successfulAnchorLegs = anchorLegs.filter(isSuccess);
  const successfulPhishingKitLegs = phishingKitLegs.filter(isSuccess);
  const recoveredAnchorLegs = anchorLegs.filter((evt) => !verdictOf(evt).capture);

  // 🔴 ANALYST-MARKED ROWS ANCHOR TOO (v1.189.0). A row the analyst marked as attacker
  // activity, with a written reason, IS attacker activity by their own determination — that
  // is the point of the mark, which exists for rows whose recorded address is a Microsoft
  // service's (an authenticator registered through a stolen session carries My Signins'
  // backend address). Like an anchoring leg it is recorded with `ip: null`: its address is
  // NOT an attacker address and must never be painted as one. Dismissed and failed rows do
  // not anchor, exactly as for every other kind.
  const analystMarks = opts?.analystEvents instanceof Map ? opts.analystEvents : new Map();
  const analystAnchors = [];
  if (analystMarks.size > 0) {
    const arrays = [signInEvents, directoryAuditEvents];
    if (allResults) {
      for (const k of ['ExchangeEvents', 'SharePointEvents', 'MessageTraceEvents', 'UrlClickEvents',
        'DlpEvents', 'GeneralEvents', 'AzureActivityEvents', 'NonInteractiveSignInEvents']) {
        arrays.push(allResults[k]);
      }
    }
    const seen = new Set();
    for (const arr of arrays) {
      for (const evt of arr || []) {
        // Attacker-verdict marks only: a Safe mark (v1.189.8) never anchors the window.
        if (!evt?.EventId || seen.has(evt.EventId)) continue;
        const mk = analystMarks.get(evt.EventId);
        if (!mk || mk.verdict === 'safe') continue;
        if (!evt.EventTimeUTC || isFailed(evt) || dismissed.has(evt.EventId)) continue;
        seen.add(evt.EventId);
        analystAnchors.push(evt);
      }
    }
  }
  const analystAnchorIds = new Set(analystAnchors.map((e) => e.EventId));

  // 🔴 ROWS ON A SESSION THE ANALYST MARKED AS THE ATTACKER'S ANCHOR TOO (v1.190.0;
  // utils/sessionMarks.js). The mark says the session was stolen from the sign-in that created
  // it, so its FIRST row starts the window -- not its first malicious-address touch, which on
  // an AiTM capture comes ~90 minutes after the relay took the session. As with a row mark,
  // these anchor with `ip: null`: the relay's address (often a residential proxy, sometimes
  // the victim's own) is NOT classified by the mark and must never be painted as an attacker
  // address. Dismissed rows do not anchor. Non-interactive rows are buckets, so both ends
  // count, exactly as for a malicious-address bucket.
  //
  // 🔴 A FAILED row on a marked session DOES count toward the window. The sign-in that creates
  // a session through an MFA flow is logged `Failed` -- it is the interrupt that asks for the
  // second factor (ResultStatusDetail 'Success'), and it already carries the session id (the
  // 19:22:59Z `Login:login` on IR-20261001-ahayman-001). The session's compromise START, which
  // drives "Pre-compromise hidden", is its first row of ANY status; a window skipping that row
  // would start 31 s after the start the case view shows. Only successful sign-ins still drive
  // the token cutoff (a failed one issued no token).
  const sessionMarks = opts?.attackerSessions instanceof Map ? opts.attackerSessions : new Map();
  const sessionAnchors = [];
  if (sessionMarks.size > 0) {
    const arrays = [signInEvents, directoryAuditEvents];
    if (allResults) {
      for (const k of ['ExchangeEvents', 'SharePointEvents', 'MessageTraceEvents', 'UrlClickEvents',
        'DlpEvents', 'GeneralEvents', 'AzureActivityEvents', 'NonInteractiveSignInEvents']) {
        arrays.push(allResults[k]);
      }
    }
    const seen = new Set();
    for (const arr of arrays) {
      for (const evt of arr || []) {
        const sid = evt?.SessionId || evt?.AADSessionId;
        if (!sid || !sessionMarks.has(sid)) continue;
        if (evt.EventId && seen.has(evt.EventId)) continue;
        if (!evt.EventTimeUTC || dismissed.has(evt.EventId)) continue;
        if (evt.EventId) seen.add(evt.EventId);
        sessionAnchors.push(evt);
      }
    }
  }
  const sessionAnchorIds = new Set(sessionAnchors.map((e) => e.EventId).filter(Boolean));

  if (maliciousIPs.length === 0 && successfulAnchorLegs.length === 0 && analystAnchors.length === 0
    && sessionAnchors.length === 0) return null;
  const anchoredByPhishingKit = maliciousIPs.length === 0 && successfulPhishingKitLegs.length > 0;
  // Did any of that capture issue a token? (v1.194.2; utils/refusedAttempts.js.) A kit row
  // logged "Success" can be an MFA step of an attempt Conditional Access then refused
  // (IR-20261013-levig-001: all 22). The window still marks when the kit was active, but the
  // panel must not imply a token or a token lifetime that never existed.
  const kitRefusal = phishingKitRefusal(successfulPhishingKitLegs, signInEvents);
  // How the window was anchored, so a renderer can SAY it rather than print "from 0
  // malicious IPs". 'recovered_transaction' = no malicious IP and no capture leg: the window
  // rests only on recovered legs whose own transaction carries exact evidence.
  // 'analyst_session' (v1.190.0) ranks ABOVE 'recovered_transaction': a marked session makes
  // the MFA legs of its own transaction the attacker's too, and naming the window after those
  // legs would print "a transaction started from more than one address" -- a claim the legs
  // may not support. The analyst's determination is what the window rests on.
  const anchorBasis = maliciousIPs.length > 0
    ? 'malicious_ip'
    : (successfulPhishingKitLegs.length > 0 ? 'phishing_kit'
      : (sessionAnchors.length > 0 ? 'analyst_session'
        : (successfulAnchorLegs.length > 0 ? 'recovered_transaction' : 'analyst_marked')));

  // Find sign-in events from malicious IPs (excluding dismissed), plus the anchoring legs
  // (deduped — a leg that also came from a classified-malicious address is one row).
  const maliciousSignIns = signInEvents.filter(
    (evt) => evt.IPAddress && maliciousSet.has(evt.IPAddress) && !dismissed.has(evt.EventId)
  );
  const seenIds = new Set(maliciousSignIns.map((e) => e.EventId).filter(Boolean));
  for (const e of anchorLegs) if (e.EventId) seenIds.add(e.EventId);
  const attackerSignIns = [
    ...maliciousSignIns,
    ...anchorLegs.filter((e) => !maliciousSignIns.includes(e)),
    // A successful sign-in on a marked session issued the attacker a token, so it drives the
    // token cutoff like any other attacker sign-in.
    ...signInEvents.filter((e) => e.EventId && sessionAnchorIds.has(e.EventId) && !seenIds.has(e.EventId)),
  ];
  const anchoringLegIds = new Set(anchorLegs.map((e) => e.EventId).filter(Boolean));
  const captureLegIds = new Set(phishingKitLegs.map((e) => e.EventId).filter(Boolean));
  const successfulRecoveredAnchors = recoveredAnchorLegs.filter(isSuccess);
  const successfulRecoveredAnchorCount = successfulRecoveredAnchors.length;
  // Per-evidence tally over the successful recovered anchors, so a report can say WHY those
  // MFA steps were attributed ("ran from an address classified malicious") without re-deriving
  // the attribution. Codes: see signInTransactions.js EVIDENCE.
  const recoveredLegEvidence = {};
  for (const evt of successfulRecoveredAnchors) {
    for (const code of verdictOf(evt).evidence) recoveredLegEvidence[code] = (recoveredLegEvidence[code] || 0) + 1;
  }

  // Only successful sign-ins produce access tokens — failed sign-ins don't extend the window
  const successfulAttackerSignIns = attackerSignIns.filter(isSuccess);

  // Sort successful sign-ins by time (these drive the TOKEN CUTOFF calculation)
  const sortedSignIns = [...successfulAttackerSignIns].sort(
    (a, b) => (a.EventTimeUTC || '').localeCompare(b.EventTimeUTC || '')
  );

  const lastAttackerSignIn = sortedSignIns.length > 0 ? sortedSignIns[sortedSignIns.length - 1].EventTimeUTC : null;

  // ── Collect ALL successful attacker activity across all event types ──
  // { time, ip } for each successful event from a malicious IP
  const activityEvents = [];

  // Sign-ins (successful only). 🔴 An anchoring leg that is NOT on a malicious address is
  // recorded with `ip: null`: its address is unclassified (for a kit landing, the VICTIM'S
  // OWN), and firstAttackerIP / lastAttackerIP are painted red as ATTACKER addresses by every
  // consumer (AttackerWindowPanel, the Compromise Summary, the exec fact pack). "Compromised at
  // <time>" with no address claimed is the safe direction. A leg on a malicious address keeps
  // it, like any malicious sign-in.
  for (const evt of successfulAttackerSignIns) {
    if (evt.EventTimeUTC) {
      const fromMaliciousIp = !!(evt.IPAddress && maliciousSet.has(evt.IPAddress));
      const viaLeg = !fromMaliciousIp && anchoringLegIds.has(evt.EventId);
      const viaSession = !fromMaliciousIp && !viaLeg && sessionAnchorIds.has(evt.EventId);
      activityEvents.push({
        time: evt.EventTimeUTC,
        ip: (viaLeg || viaSession) ? null : evt.IPAddress,
        phishingKit: viaLeg && captureLegIds.has(evt.EventId),
        ...(viaSession ? { sessionMarked: true } : {}),
      });
    }
  }

  // Attacker activity that is NOT an interactive sign-in. Any of it means a token existed, so
  // it is what rules out "no token was issued" (attackerAccess.js).
  let otherActivityCount = 0;

  // All other event types — any non-failed event from a malicious IP counts
  if (allResults) {
    const otherArrays = [
      allResults.ExchangeEvents,
      allResults.SharePointEvents,
      allResults.MessageTraceEvents,
      allResults.UrlClickEvents,
      allResults.DlpEvents,
      allResults.GeneralEvents,
      allResults.AzureActivityEvents,
      // SecurityAlertEvents and SecurityIncidentEvents excluded — their timestamps
      // represent when Microsoft detected/created the alert, not when the attacker
      // was active. Including them would incorrectly extend the attacker window.
      // RiskDetectionEvents also excluded — detection timestamps, not activity.
    ];

    for (const arr of otherArrays) {
      if (!arr) continue;
      for (const evt of arr) {
        if (!evt.EventTimeUTC || isFailed(evt)) continue;
        if (dismissed.has(evt.EventId)) continue;
        const malIP = getMaliciousIP(evt, maliciousSet);
        if (malIP) {
          activityEvents.push({ time: evt.EventTimeUTC, ip: malIP });
          otherActivityCount += 1;
        }
      }
    }

    // 🔴 THE NON-INTERACTIVE FEED, ADDED v1.172.0. It was absent, so a token used from a
    // malicious address did not count as attacker activity and could not extend the
    // window. That fails in the DANGEROUS direction: the window closes at the last
    // INTERACTIVE event, and the post-window pill then HIDES the replay that happened
    // after it — the portal concealing the continuation of the very access it is
    // reporting. Token replay is the whole reason this feed exists.
    //
    // Only rows from an address the ANALYST classified malicious count, so this is not a
    // widening of what "attacker activity" means — a malicious address refreshing a token
    // is attacker activity by construction.
    //
    // 🔴 BOTH ENDS OF THE BUCKET. A row is an AGGREGATE over (session, IP, hour) carrying
    // `EventCount`, so its `EventTimeUTC` is the FIRST authentication and
    // `LastEventTimeUTC` the last. Pushing only the first would stop the window up to an
    // hour short of activity the row itself records.
    for (const evt of (allResults.NonInteractiveSignInEvents || [])) {
      if (!evt.EventTimeUTC || isFailed(evt)) continue;
      if (dismissed.has(evt.EventId)) continue;
      const malIP = getMaliciousIP(evt, maliciousSet);
      if (!malIP) continue;
      activityEvents.push({ time: evt.EventTimeUTC, ip: malIP });
      otherActivityCount += 1;
      if (evt.LastEventTimeUTC && evt.LastEventTimeUTC !== evt.EventTimeUTC) {
        activityEvents.push({ time: evt.LastEventTimeUTC, ip: malIP });
      }
    }
  }

  // Analyst-marked rows (see analystAnchors above). A row already counted from a malicious
  // address is not pushed twice — it is the same instant either way, but the malicious
  // address is the stronger claim and keeps its IP.
  for (const evt of analystAnchors) {
    if (getMaliciousIP(evt, maliciousSet)) continue;
    activityEvents.push({ time: evt.EventTimeUTC, ip: null, analystMarked: true });
    if (!signInEvents.includes(evt)) otherActivityCount += 1;
  }

  // Marked-session rows (see sessionAnchors above). Sign-ins were pushed with the attacker
  // sign-ins; a row already counted from a malicious address keeps that stronger claim.
  for (const evt of sessionAnchors) {
    // A successful sign-in was pushed with the attacker sign-ins above; a failed one was not.
    if (signInEvents.includes(evt) && isSuccess(evt)) continue;
    if (getMaliciousIP(evt, maliciousSet)) continue;
    if (analystAnchorIds.has(evt.EventId)) continue;
    activityEvents.push({ time: evt.EventTimeUTC, ip: null, sessionMarked: true });
    if (!signInEvents.includes(evt)) otherActivityCount += 1;
    if (evt.LastEventTimeUTC && evt.LastEventTimeUTC !== evt.EventTimeUTC) {
      activityEvents.push({ time: evt.LastEventTimeUTC, ip: null, sessionMarked: true });
    }
  }

  // Sort all activity by time
  activityEvents.sort((a, b) => a.time.localeCompare(b.time));

  const firstActivity = activityEvents.length > 0 ? activityEvents[0] : null;
  const lastActivity = activityEvents.length > 0 ? activityEvents[activityEvents.length - 1] : null;

  // Find remediation events from directory audit
  let accountDisabledAt = null;
  let accountDisabledBy = null;
  let tokensRevokedAt = null;
  let tokensRevokedBy = null;
  let passwordResetAt = null;
  let passwordResetBy = null;

  // 🔴 EVERY containment event, not just the earliest of each type (v1.183.0). The
  // earliest-of-each-type fields above answer "when was it first contained"; episode
  // splitting needs each containment INSTANT, because a case can hold two compromises weeks
  // apart, each remediated in turn (IR-20260911-clarionhealth-001: disabled + reset on Jul 30,
  // then a fresh phishing-kit compromise on Sep 11, again contained the same day).
  const containmentEvents = [];

  // 🔴 A STEP TAKEN BEFORE THE ATTACK BEGAN IS NOT CONTAINMENT (v1.204.4). On
  // IR-...-kwilliams2 a password change on Jun 15 was listed as "Password changed ✓" under
  // "When it was contained", and marked the case contained in the severity line, for an attack
  // that began Jun 17. The attack begins at the attacker's earliest recorded row of ANY outcome
  // -- a refused sign-in attempt counts, so a password changed between the attempts and the
  // first success is still containment. With nothing to compare against, every step counts.
  let attackStartMs = Infinity;
  for (const t of [
    ...activityEvents.map((a) => a.time),
    ...attackerSignIns.filter((e) => !dismissed.has(e.EventId)).map((e) => e.EventTimeUTC),
  ]) {
    const ms = t ? Date.parse(t) : NaN;
    if (ms < attackStartMs) attackStartMs = ms;
  }
  const beforeAttack = (time) => Number.isFinite(attackStartMs) && Date.parse(time) < attackStartMs;
  const attackStartedAt = Number.isFinite(attackStartMs) ? new Date(attackStartMs).toISOString() : null;

  directoryAuditEvents.forEach((evt) => {
    const op = (evt.Operation || '').toLowerCase();
    const time = evt.EventTimeUTC;
    const actor = evt.UserPrincipalName || evt.InitiatedBy || '';
    if (!time || beforeAttack(time)) return;

    if (DISABLE_OPS.some((d) => op.includes(d))) {
      containmentEvents.push({ time, type: 'account_disabled', actor });
      if (!accountDisabledAt || time < accountDisabledAt) {
        accountDisabledAt = time;
        accountDisabledBy = actor;
      }
    }
    if (REVOKE_OPS.some((r) => op.includes(r))) {
      containmentEvents.push({ time, type: 'tokens_revoked', actor });
      if (!tokensRevokedAt || time < tokensRevokedAt) {
        tokensRevokedAt = time;
        tokensRevokedBy = actor;
      }
    }
    if (PASSWORD_OPS.some((p) => op.includes(p))) {
      containmentEvents.push({ time, type: 'password_reset', actor });
      if (!passwordResetAt || time < passwordResetAt) {
        passwordResetAt = time;
        passwordResetBy = actor;
      }
    }
  });

  // ── Split attacker activity into remediation-separated EPISODES (v1.183.0) ──────────────
  // An episode is a run of attacker activity that is not interrupted by containment. It
  // closes when a containment action (account disabled / tokens revoked / password reset)
  // occurs after its most recent activity and before the next attacker activity — access was
  // killed and later re-established by a fresh compromise. The active (most recent) episode is
  // the last one, contained iff any containment follows its last activity.
  //
  // 🔴 This is DESCRIPTIVE ONLY. The single-window fields below (firstAttackerActivity,
  // lastAttackerActivity, globalCutoff) are UNCHANGED, so the pill filters, session logic and
  // the exec-report reconcile ceilings behave exactly as before; only the human-readable
  // description reads two short contained episodes instead of one 43-day span.
  // At the same instant a disable + revoke + reset often land together; prefer the clearest
  // statement of what ended access (disabled > revoked > reset) for the episode's label.
  const CONTAIN_PRIORITY = { account_disabled: 0, tokens_revoked: 1, password_reset: 2 };
  const sortedContainment = [...containmentEvents].sort(
    (a, b) => a.time.localeCompare(b.time) || (CONTAIN_PRIORITY[a.type] - CONTAIN_PRIORITY[b.type])
  );
  // 🔴 Only a DISABLE or PASSWORD RESET starts a new episode. A bare token revoke does NOT:
  // an attacker who still holds the password just re-authenticates, so activity continuing
  // past a lone revoke is the SAME compromise (token replay / re-auth), not a fresh one. A
  // new episode requires the access to have been cut such that re-entry needs a fresh capture
  // — which is what disabling the account or resetting the password does.
  const STRONG_CONTAINMENT = new Set(['account_disabled', 'password_reset']);
  const firstContainmentAfter = (afterTime, beforeTime, strongOnly = false) => {
    for (const c of sortedContainment) {
      if (strongOnly && !STRONG_CONTAINMENT.has(c.type)) continue;
      if (c.time > afterTime && (!beforeTime || c.time < beforeTime)) return c;
    }
    return null;
  };
  const countSuccessBetween = (list, start, end) =>
    list.filter((e) => e.EventTimeUTC && e.EventTimeUTC >= start && e.EventTimeUTC <= end).length;

  // 🔴 RESIDUAL ACTIVITY IS NOT A NEW EPISODE (v1.203.0). A disable or reset does not end
  // access instantly: a token already issued keeps working for its lifetime (the same
  // allowance the cutoff below applies). Activity that falls entirely inside that allowance,
  // up to the next strong containment, is the SAME compromise still running down. On
  // IR-20260818-Controller-001 a single event 12 minutes after the 11:40 reset opened a
  // "second episode" that the next containment, 15 days later, then "ended" -- and both
  // reports told the client the attacker had a second 15-day run. A real re-entry still
  // splits: activity that outlasts the allowance needed a fresh compromise
  // (IR-20260922-tdarlington-001: restarted 60 min after a reset, then ran 17 hours).
  const allowanceMinutes = (type) => (type === 'account_disabled'
    ? CAE_PROPAGATION_MINUTES + BUFFER_MINUTES : tokenLifetimeMinutes);
  const isResidualAfter = (containment, fromIdx) => {
    const limit = Date.parse(containment.time) + allowanceMinutes(containment.type) * 60000;
    for (let k = fromIdx; k < activityEvents.length; k += 1) {
      if (k > fromIdx && firstContainmentAfter(activityEvents[k - 1].time, activityEvents[k].time, true)) break;
      if (Date.parse(activityEvents[k].time) > limit) return false;
    }
    return true;
  };

  const episodes = [];
  if (activityEvents.length > 0) {
    let epStart = activityEvents[0].time;
    let epLast = activityEvents[0].time;
    // The containment that ended this episode when later activity was only residual.
    let residualOf = null;
    const close = (contained) => {
      const tail = residualOf || firstContainmentAfter(epLast, null);
      const c = contained || tail;
      episodes.push({
        start: epStart,
        end: epLast,
        contained: !!c,
        containedAt: c ? c.time : null,
        containmentType: c ? c.type : null,
        signInCount: countSuccessBetween(successfulAttackerSignIns, epStart, epLast),
        kitLegCount: countSuccessBetween(successfulPhishingKitLegs, epStart, epLast),
      });
    };
    for (let i = 1; i < activityEvents.length; i += 1) {
      const t = activityEvents[i].time;
      // Boundary: only a STRONG containment (disable/reset) between the last activity and this
      // one starts a new episode. The exposure-end (containedAt) still uses the earliest
      // containment of ANY type after the episode's activity.
      const boundary = firstContainmentAfter(epLast, t, true);
      if (boundary && isResidualAfter(boundary, i)) {
        // Still the same compromise: keep the episode, and keep the containment that ended it.
        if (!residualOf) residualOf = firstContainmentAfter(epLast, t) || boundary;
        epLast = t;
      } else if (boundary) {
        // The exposure end is the EARLIEST containment of any type after this episode's last
        // activity and before the next one — a revoke that preceded the disable ends it first.
        const end = residualOf || firstContainmentAfter(epLast, t) || boundary;
        episodes.push({
          start: epStart,
          end: epLast,
          contained: true,
          containedAt: end.time,
          containmentType: end.type,
          signInCount: countSuccessBetween(successfulAttackerSignIns, epStart, epLast),
          kitLegCount: countSuccessBetween(successfulPhishingKitLegs, epStart, epLast),
        });
        epStart = t;
        epLast = t;
        residualOf = null;
      } else {
        epLast = t;
      }
    }
    close(false);
  }
  const episodeCount = episodes.length;

  // ── What the attempts achieved, and the account's end state (attackerAccess.js) ─────────
  // 🔴 GENERAL, not kit-only. v1.194.2 answered "was a token issued?" only for a window
  // anchored on phishing-kit steps with NO malicious IP. Classifying the relay addresses
  // malicious (correct) then switched it off, and the Executive Brief told a client the
  // attacker "briefly accessed" an account no token was ever issued for (IR-20261013-levig-001).
  // `noTokenIssued` now holds for ANY window whose attacker sign-ins all failed to issue a
  // token and that holds no other attacker activity. A kit-anchored window additionally keeps
  // the v1.194.2 requirement that every kit row belongs to a refused attempt.
  const access = assessAttackerAccess({
    attackerSignIns, allSignIns: signInEvents, otherActivityCount,
  });
  const noTokenIssued = access.noTokenIssued && (!anchoredByPhishingKit || kitRefusal.allRefused);
  // 🔴 Refused attempts are not compromises, so they are never "separate episodes" (v1.203.0).
  // IR-20260622-rbertucio-001 (6 attempts, all refused) got a Brief reading "2 separate
  // episodes -- The account was compromised more than once" above "All 6 sign-in attempts were
  // refused". Every consumer (both reports, the model's facts, the severity scope) reads this flag.
  const multiEpisode = episodeCount > 1 && !noTokenIssued;
  const accountState = accountStateHistory(directoryAuditEvents);
  const accessFields = {
    noTokenIssued,
    attackerAccess: { ...access, noTokenIssued },
    accountState,
  };

  // Calculate cutoff candidates (still based on sign-in + token lifetime and remediation)
  const candidates = [];

  if (accountDisabledAt) {
    candidates.push({
      time: addMinutes(accountDisabledAt, CAE_PROPAGATION_MINUTES + BUFFER_MINUTES),
      method: `Account disabled + ${CAE_PROPAGATION_MINUTES + BUFFER_MINUTES} min (CAE propagation + buffer)`,
      anchor: 'account_disabled', anchorAt: accountDisabledAt,
      allowance: CAE_PROPAGATION_MINUTES + BUFFER_MINUTES,
    });
  }

  if (tokensRevokedAt) {
    candidates.push({
      time: addMinutes(tokensRevokedAt, tokenLifetimeMinutes),
      method: `Tokens revoked + ${tokenLifetimeMinutes} min token lifetime`,
      anchor: 'tokens_revoked', anchorAt: tokensRevokedAt, allowance: tokenLifetimeMinutes,
    });
  }

  if (passwordResetAt) {
    candidates.push({
      time: addMinutes(passwordResetAt, tokenLifetimeMinutes),
      method: `Password reset + ${tokenLifetimeMinutes} min token lifetime`,
      anchor: 'password_reset', anchorAt: passwordResetAt, allowance: tokenLifetimeMinutes,
    });
  }

  if (lastAttackerSignIn) {
    candidates.push({
      time: addMinutes(lastAttackerSignIn, tokenLifetimeMinutes),
      method: `Last attacker sign-in + ${tokenLifetimeMinutes} min token lifetime`,
      anchor: 'last_signin', anchorAt: lastAttackerSignIn, allowance: tokenLifetimeMinutes,
    });
  }

  // If malicious IPs exist but no activity and no remediation → indeterminate
  if (candidates.length === 0 && activityEvents.length === 0) {
    return {
      maliciousIPs,
      attackerSignInCount: attackerSignIns.length,
      successfulAttackerSignInCount: successfulAttackerSignIns.length,
      phishingKitLegCount: phishingKitLegs.length,
      successfulPhishingKitLegCount: successfulPhishingKitLegs.length,
      phishingKitRefusedLegCount: kitRefusal.refusedLegs,
      phishingKitNoTokenIssued: anchoredByPhishingKit && kitRefusal.allRefused,
      ...accessFields,
      anchoredByPhishingKit,
      anchorBasis,
      anchoringLegIds,
      captureLegIds,
      recoveredLegAnchorCount: recoveredAnchorLegs.length,
      successfulRecoveredLegAnchorCount: successfulRecoveredAnchorCount,
      recoveredLegEvidence,
      // Rows the analyst marked as attacker activity that anchor the window (v1.189.0).
      analystAnchorIds,
      analystAnchorCount: analystAnchorIds.size,
      sessionMarkAnchorCount: sessionAnchorIds.size,
      firstAttackerActivity: null,
      firstAttackerIP: null,
      lastAttackerActivity: null,
      lastAttackerIP: null,
      // Keep legacy names for backward compat with Attacker Window Panel
      firstAttackerSignIn: null,
      lastAttackerSignIn: null,
      accountDisabledAt,
      accountDisabledBy,
      tokensRevokedAt,
      tokensRevokedBy,
      passwordResetAt,
      passwordResetBy,
      globalCutoff: null,
      cutoffMethod: null,
      cutoffLabel: null,
      cutoffAnchor: '',
      cutoffAnchorAt: null,
      cutoffAllowanceMinutes: tokenLifetimeMinutes,
      tokenLifetimeUsed: tokenLifetimeMinutes,
      isIndeterminate: true,
      containmentEvents: sortedContainment,
      attackStartedAt,
      episodes: [],
      episodeCount: 0,
      multiEpisode: false,
    };
  }

  // If no cutoff candidates but we have activity, use last activity + token lifetime
  if (candidates.length === 0 && lastActivity) {
    candidates.push({
      time: addMinutes(lastActivity.time, tokenLifetimeMinutes),
      method: `Last attacker activity + ${tokenLifetimeMinutes} min token lifetime`,
      anchor: 'last_activity', anchorAt: lastActivity.time, allowance: tokenLifetimeMinutes,
    });
  }

  // Global cutoff = worst case (latest) across all candidates
  candidates.sort((a, b) => b.time - a.time);
  const globalCutoff = candidates[0].time.toISOString();
  const cutoffMethod = candidates[0].method;
  // 🔴 A STABLE ENUM plus the anchor instant and the allowance, so a renderer can SAY the
  // derivation without parsing `cutoffMethod`'s prose. The cutoff is a MODELLED endpoint, not an
  // observed event -- "Access is treated as ending Aug 3, 1:10 PM" matched nothing in the case
  // and appeared to contradict the containment block's 11:40 AM until the arithmetic was stated.
  const cutoffAnchor = candidates[0].anchor || '';
  const cutoffAnchorAt = candidates[0].anchorAt
    ? new Date(candidates[0].anchorAt).toISOString() : null;
  const cutoffAllowanceMinutes = candidates[0].allowance ?? tokenLifetimeMinutes;

  // Build a human-readable label
  const parts = [];
  if (accountDisabledAt) parts.push('account disabled');
  if (tokensRevokedAt) parts.push('tokens revoked');
  if (passwordResetAt) parts.push('password reset');
  if (!accountDisabledAt && !tokensRevokedAt && !passwordResetAt) {
    parts.push(lastAttackerSignIn ? 'last attacker sign-in' : 'last attacker activity');
  }
  const cutoffLabel = parts.length > 0
    ? `Based on ${parts.join(' + ')}`
    : cutoffMethod;

  return {
    maliciousIPs,
    attackerSignInCount: attackerSignIns.length,
    successfulAttackerSignInCount: successfulAttackerSignIns.length,
    // AiTM CAPTURE (v1.181.0; capture-only since v1.188.0): how many phishing-kit landing
    // legs (URL-shaped application) took part, and whether they are the ONLY anchor (no IP
    // classified malicious). Consumers use this to SAY how the window was anchored rather than
    // printing "from 0 malicious IPs".
    phishingKitLegCount: phishingKitLegs.length,
    successfulPhishingKitLegCount: successfulPhishingKitLegs.length,
    phishingKitRefusedLegCount: kitRefusal.refusedLegs,
    phishingKitNoTokenIssued: anchoredByPhishingKit && kitRefusal.allRefused,
    ...accessFields,
    anchoredByPhishingKit,
    // Anchoring legs (v1.188.0): every non-dismissed leg with the `attacker` verdict, capture or
    // not. The exec fact pack and the reports select rows with these Sets — never by
    // re-applying a shape predicate. The attribution itself lives on CaseDetail's
    // `legAttribution`, not here: the window can be null and the verdicts must survive that.
    anchorBasis,
    anchoringLegIds,
    captureLegIds,
    recoveredLegAnchorCount: recoveredAnchorLegs.length,
    successfulRecoveredLegAnchorCount: successfulRecoveredAnchorCount,
    recoveredLegEvidence,
    // Rows the analyst marked as attacker activity that anchor the window (v1.189.0).
    analystAnchorIds,
    analystAnchorCount: analystAnchorIds.size,
    sessionMarkAnchorCount: sessionAnchorIds.size,
    // Activity window (all event types)
    firstAttackerActivity: firstActivity?.time || null,
    firstAttackerIP: firstActivity?.ip || null,
    lastAttackerActivity: lastActivity?.time || null,
    lastAttackerIP: lastActivity?.ip || null,
    // Legacy sign-in-only fields (for Attacker Window Panel backward compat)
    firstAttackerSignIn: sortedSignIns.length > 0 ? sortedSignIns[0].EventTimeUTC : null,
    lastAttackerSignIn,
    accountDisabledAt,
    accountDisabledBy,
    tokensRevokedAt,
    tokensRevokedBy,
    passwordResetAt,
    passwordResetBy,
    globalCutoff,
    cutoffMethod,
    cutoffLabel,
    // 🔴 PROJECT THEM. Computed above and omitted here is this project's recurring shape (the
    // message-trace SilverRecordId was assigned on the object and never named in the output for
    // four versions). Caught immediately because the rebuilt pack showed cutoffAnchor empty.
    cutoffAnchor,
    cutoffAnchorAt,
    cutoffAllowanceMinutes,
    tokenLifetimeUsed: tokenLifetimeMinutes,
    isIndeterminate: false,
    // 🔴 EVERY containment INSTANT, sorted (v1.184.0). The earliest-of-each-type fields above
    // (accountDisabledAt/tokensRevokedAt/passwordResetAt) answer "when was it FIRST contained",
    // which is a single-incident question: on a remediation-separated case they all resolve to
    // episode 1, so the Brief's "when it was contained" column showed only episode 1's Jul 30
    // disable/revoke/reset and silently dropped episode 2's own Sep 11 disable + reset. The Brief
    // renderer lists every occurrence per action from this array. Each: { time, type, actor }.
    containmentEvents: sortedContainment,
    // The attacker's earliest recorded row of any outcome (v1.204.4): steps before it are not
    // containment, and the exec pack uses it to keep them out of "Mitigation steps taken".
    attackStartedAt,
    // Remediation-separated episodes (v1.183.0). Each: { start, end, contained, containedAt,
    // containmentType, signInCount, kitLegCount }. The single-window fields above still span
    // ALL activity; consumers use these to describe multiple contained compromises accurately.
    episodes,
    episodeCount,
    multiEpisode,
  };
}

/**
 * Checks if an event falls after the attacker window cutoff
 * and is NOT from a malicious IP.
 *
 * Events that should NOT be hidden:
 * - Events from malicious IPs (always show attacker activity)
 * - Events before the cutoff
 * - Directory audit events (remediation actions)
 * - Events where any resolved IP is malicious (multi-IP sessions)
 *
 * @param {Object} evt - Event object
 * @param {string} globalCutoff - ISO timestamp of the window cutoff
 * @param {Set} maliciousSet - Set of malicious IP strings
 * @returns {boolean} true if the event is a post-window safe event (can be hidden)
 */
export function isPostWindowEvent(evt, globalCutoff, maliciousSet) {
  if (!globalCutoff || !evt.EventTimeUTC) return false;
  if (evt.EventTimeUTC <= globalCutoff) return false;

  // Check primary IP
  const ip = evt.IPAddress || '';
  if (ip && maliciousSet.has(ip)) return false;

  // Check inferred IP
  const inferredIP = evt.InferredIP || '';
  if (inferredIP && maliciousSet.has(inferredIP)) return false;

  // Check multi-IP resolved events — keep if ANY resolved IP is malicious
  if (evt.AllResolvedIPs) {
    const resolved = evt.AllResolvedIPs.split(',').map((s) => s.trim());
    if (resolved.some((rip) => maliciousSet.has(rip))) return false;
  }

  return true;
}

/**
 * Checks if an event falls STRICTLY BEFORE the attacker window opened and is NOT
 * from a malicious IP — the mirror of isPostWindowEvent at the other end.
 *
 * 🔴 WHY THIS EXISTS SEPARATELY FROM `isPreCompromiseSessionEvent` (v1.171.0).
 * That one is SESSION-scoped: it needs the event to carry a session id, that session
 * to be one a malicious-IP event touched, and the event to predate that session's
 * compromise moment. An event with no session id, or on a session the attacker never
 * touched, is invisible to it — so on a real case (IR-20260826-gwoodward-001) 98
 * SharePoint file-access rows and 100 Exchange rows sat in the curated view from
 * hours or days before the attacker arrived, and no pill could reach them. The
 * analyst had not marked their IPs as an earlier compromise point precisely because
 * they had identified them as the real user's own addresses.
 *
 * 🔴 AND WHY IT IS A PILL, DEFAULT OFF, NOT A STANDING FILTER. The established
 * compromise point is a SEED, not proof: activity before it is a LEAD (an earlier
 * foothold, a second account, a staged phish), not an analyst error. Hiding it by
 * default would delete exactly the evidence that moves a window earlier. The pill is
 * the analyst saying, at the end, that they looked and it is not part of the story.
 *
 * Events that are NEVER hidden by this:
 * - Events from a malicious IP, or whose session resolved to one (multi-IP)
 * - Events at or after the window start (the first attacker event itself included)
 * - Directory audit events — the caller does not apply this to them, because
 *   remediation actions stay visible regardless of when they happened
 *
 * @param {Object} evt - Event object
 * @param {string} windowStart - ISO timestamp of the first attacker activity
 * @param {Set} maliciousSet - Set of malicious IP strings
 * @returns {boolean} true if the event predates the window and looks non-attacker
 */
export function isPreWindowEvent(evt, windowStart, maliciousSet, exemptIds) {
  if (!windowStart || !evt.EventTimeUTC) return false;
  // The analyst's own entry-point findings (see analystEntryPointIds). A lure arrives
  // BEFORE the window by definition, so without this the pill always hides it.
  if (exemptIds && exemptIds.has(String(evt.EventId || ''))) return false;
  // STRICTLY before: the first attacker event is the window, not something before it.
  if (evt.EventTimeUTC >= windowStart) return false;

  const ip = evt.IPAddress || '';
  if (ip && maliciousSet.has(ip)) return false;

  const inferredIP = evt.InferredIP || '';
  if (inferredIP && maliciousSet.has(inferredIP)) return false;

  // Multi-IP resolved events — keep if ANY resolved IP is malicious. A pre-window event
  // whose session also resolved to the attacker's address is the earlier-foothold case
  // this filter must never swallow.
  if (evt.AllResolvedIPs) {
    const resolved = evt.AllResolvedIPs.split(',').map((s) => s.trim());
    if (resolved.some((rip) => maliciousSet.has(rip))) return false;
  }

  return true;
}

/**
 * Event ids the analyst marked as the way in: message-trace rows flagged as phishing
 * (`phishingFlags[].eventId`, the composite `<silver-id>|<recipient>` id) and URL clicks
 * classified `confirmed` or `suspected`. The "Before window" pill never hides these:
 * the window opens at the first attacker activity, and the lure and its click come
 * before that by definition, so hiding everything earlier hid the analyst's own finding
 * (IR-20261013-levig-001, 2026-10-14). Dismissal still applies.
 */
export function analystEntryPointIds(phishingFlags, urlClassifications) {
  const ids = new Set();
  for (const f of (Array.isArray(phishingFlags) ? phishingFlags : [])) {
    const id = String(f?.eventId || '').trim();
    if (id) ids.add(id);
  }
  for (const [id, cls] of Object.entries(urlClassifications || {})) {
    if (cls === 'confirmed' || cls === 'suspected') ids.add(String(id));
  }
  return ids;
}

// ─────────────────────────────────────────────────────────────────────────
// Compromised-session helpers — used by CaseDetail's filter pipeline and by
// TimelineView for the row badge. A "compromised session" is one whose
// SessionId / AADSessionId was touched by an event from a malicious IP
// inside the case. The compromise start is the earliest event time across
// any such touch — pre-compromise events are legitimate prior session use,
// post-compromise events could be the attacker on a stolen token.
//
// We use a unified namespace: sign-in `SessionId` and Exchange/SharePoint
// `AADSessionId` — Microsoft typically issues the same GUID for both layers
// of the same logical session. If they ever differ we just don't propagate
// compromise across that boundary; we never over-hide.
// ─────────────────────────────────────────────────────────────────────────

function eventSessionId(evt) {
  return evt && (evt.SessionId || evt.AADSessionId) ? (evt.SessionId || evt.AADSessionId) : null;
}

/**
 * Returns true iff the event's session is compromised AND the event time
 * is strictly before the session's compromise start. These are the legit
 * pre-compromise events the analyst typically wants hidden.
 */
export function isPreCompromiseSessionEvent(evt, compromisedSessions) {
  if (!evt || !compromisedSessions || compromisedSessions.size === 0) return false;
  const sid = eventSessionId(evt);
  if (!sid) return false;
  const start = compromisedSessions.get(sid);
  if (!start) return false;
  return evt.EventTimeUTC && evt.EventTimeUTC < start;
}

/**
 * Returns true iff the event's session is compromised AND the event time
 * is at or after the session's compromise start. These are the events the
 * analyst should always be able to see (modulo their explicit choices) —
 * they could be the attacker using a stolen token even when the IP looks
 * clean.
 */
export function isPostCompromiseSessionEvent(evt, compromisedSessions) {
  if (!evt || !compromisedSessions || compromisedSessions.size === 0) return false;
  const sid = eventSessionId(evt);
  if (!sid) return false;
  const start = compromisedSessions.get(sid);
  if (!start) return false;
  return evt.EventTimeUTC && evt.EventTimeUTC >= start;
}

/**
 * Override gate for the Safe-IP / Service-IP / Email-only filter family.
 * An event qualifies for the override iff it's post-compromise session-tied
 * AND the analyst hasn't *explicitly* suppressed it:
 *
 *   - Its `IPAddress` is classified as `safe` in `ipClassifications` →
 *     analyst made an explicit safe call. Respect it.
 *   - Its `EventId` is in `dismissedEventIds` → analyst dismissed this
 *     specific row. Respect it.
 *
 * Implicit-safe categorization (tenant safe IPs, service IPs, known-app IPs,
 * email-only filter, etc.) does NOT block the override — that's the whole
 * point: we want to surface attacker-on-stolen-session activity that would
 * otherwise be hidden as "looks like the user."
 */
export function shouldOverrideHideForSessionCompromise(evt, compromisedSessions, ipClassifications, dismissedEventIds) {
  if (!isPostCompromiseSessionEvent(evt, compromisedSessions)) return false;
  const ip = evt && (evt.IPAddress || evt.ResolvedUserIP);
  if (ip && ipClassifications && ipClassifications[ip] === 'safe') return false;
  if (evt && evt.EventId && dismissedEventIds && dismissedEventIds.has && dismissedEventIds.has(evt.EventId)) return false;
  return true;
}

/**
 * Derives the set of session ids that are "safe-IP-anchored": a session qualifies
 * iff at least one of its events was seen from an analyst-Safe IP, AND the session
 * never touched a malicious IP (i.e. it is NOT in `compromisedSessions`). This
 * propagates an analyst "Safe IP" determination across the whole session, so
 * session-bound events whose own IP is an unresolvable Microsoft service IP can be
 * collapsed as the user's own activity — without ever sweeping in a session the
 * attacker also rode (that lands in `compromisedSessions` and is excluded here).
 *
 * The exclusion of compromised sessions is the entire safety mechanism, and it
 * self-corrects: classify an attacker IP malicious and any session it touched drops
 * out, so its events reappear. Sign-in events participate as anchors here, but the
 * consumer (CaseDetail Phase 3.7) does not hide sign-ins — they are the session's
 * location evidence.
 *
 * Pure / side-effect-free for testability. IP membership uses the same exact-string
 * semantics as the Safe-IP filter (`effectiveSafeIPSet.has(...)`).
 *
 * @param {object} results Unfiltered results object with PascalCase event arrays.
 * @param {Set<string>} safeIpSet Effective Safe IP set (CaseDetail `effectiveSafeIPSet`).
 * @param {Map<string,string>} compromisedSessions session id -> earliest malicious-IP touch.
 * @returns {Set<string>} safe-IP-anchored session ids (contested sessions excluded).
 */
export function deriveSafeIpSessionSet(results, safeIpSet, compromisedSessions) {
  const set = new Set();
  if (!results || !safeIpSet || safeIpSet.size === 0) return set;
  const comp = compromisedSessions || new Map();
  const touchesSafeIp = (evt) => {
    if (evt.IPAddress && safeIpSet.has(evt.IPAddress)) return true;
    if (evt.ResolvedUserIP && safeIpSet.has(evt.ResolvedUserIP)) return true;
    if (evt.AllResolvedIPs) {
      for (const raw of String(evt.AllResolvedIPs).split(',')) {
        const ip = raw.trim();
        if (ip && safeIpSet.has(ip)) return true;
      }
    }
    return false;
  };
  const collect = (events) => {
    if (!events) return;
    for (const evt of events) {
      if (!touchesSafeIp(evt)) continue;
      const sid = eventSessionId(evt);
      if (!sid) continue;
      if (comp.has(sid)) continue; // contested — attacker also rode this session; never hide
      set.add(sid);
    }
  };
  collect(results.SignInEvents);
  collect(results.ExchangeEvents);
  collect(results.SharePointEvents);
  collect(results.MessageTraceEvents);
  collect(results.UrlClickEvents);
  collect(results.DlpEvents);
  collect(results.GeneralEvents);
  collect(results.AzureActivityEvents);
  collect(results.RiskDetectionEvents);
  collect(results.RiskyUserEvents);
  collect(results.SecurityAlertEvents);
  return set;
}
